import { useEffect, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { qk } from "@/lib/query-keys";
import { contarReporte, previewReporte, type RepDefinicion } from "../services/reporteria.api";
import { esEjecutable, firmaDef } from "../state/builder-ops";

/** Filas por bloque · scroll infinito. */
export const TAMANO_BLOQUE = 100;
/** Bloques que se conservan en memoria (ventana LRU · memoria acotada · §5.2). */
const MAX_BLOQUES = 6;
/** Espera antes de disparar el preview tras el último cambio de la definición. */
const DEBOUNCE_MS = 400;

/** El candado por usuario (RN-08) responde 409 si hay otra consulta en curso. */
function esConflicto(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 409;
}

function useDebounced<T>(valor: T, ms: number): T {
  const [debounced, setDebounced] = useState(valor);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(valor), ms);
    return () => clearTimeout(id);
  }, [valor, ms]);
  return debounced;
}

/**
 * Preview con SCROLL INFINITO y CONTEO DIFERIDO (§5.2). La definición se
 * DEBOUNCEA (no se dispara una consulta por cada tecleo/clic). Cada bloque hace
 * POST /preview con incluirTotal=false; TanStack Query mantiene una ventana de a
 * lo más MAX_BLOQUES páginas (`maxPages`), acotando la memoria. El total va
 * aparte, una sola vez, cacheado por la firma. El bloque y el total son consultas
 * PESADAS serializadas por el candado (RN-08); si chocan, el perdedor recibe 409 y
 * se reintenta con backoff hasta que el otro libera.
 */
export function usePreviewInfinito(def: RepDefinicion, activo: boolean) {
  const defDebounced = useDebounced(def, DEBOUNCE_MS);
  const firma = firmaDef(defDebounced);
  const habilitado = activo && esEjecutable(defDebounced);

  const preview = useInfiniteQuery({
    queryKey: qk.repPreview(firma),
    enabled: habilitado,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      previewReporte({ def: defDebounced, pagina: pageParam, tamano: TAMANO_BLOQUE, incluirTotal: false, muestra: false }),
    getNextPageParam: (ultima, paginas) =>
      ultima.filas.length < TAMANO_BLOQUE ? undefined : paginas.length + 1,
    getPreviousPageParam: (_primera, _paginas, primerParam) =>
      primerParam > 1 ? primerParam - 1 : undefined,
    maxPages: MAX_BLOQUES,
    staleTime: 30_000,
    retry: (n, error) => esConflicto(error) && n < 5,
    retryDelay: (n) => 300 * (n + 1),
  });

  // El conteo espera a que el primer bloque del preview cargue: así el bloque y
  // el conteo (ambos consultas PESADAS · RN-08) NO chocan en el candado.
  const total = useQuery({
    queryKey: qk.repTotal(firma),
    enabled: habilitado && preview.isSuccess,
    queryFn: () => contarReporte(defDebounced),
    staleTime: 60_000,
    retry: (n, error) => esConflicto(error) && n < 5,
    retryDelay: (n) => 300 * (n + 1),
  });

  const columnas = preview.data?.pages[0]?.columnas ?? [];
  const filas = preview.data?.pages.flatMap((p) => p.filas) ?? [];
  // Los totales (columnas calculadas de tipo agregado) son los mismos en cada
  // bloque · se calculan sobre TODO el resultado (RN-18).
  const totales = preview.data?.pages[0]?.totales ?? null;

  return { preview, total, columnas, filas, totales };
}

/** Shape del preview (usuario o admin en contexto de config) que consume PreviewTabla. */
export type PreviewInfinito = ReturnType<typeof usePreviewInfinito>;
